import axios, { AxiosError, AxiosHeaders } from 'axios';
import {
  AuthSessionSnapshot,
  CreatorEventFilters,
  Event,
  EventCatalogs,
  EventFormValues,
  LoginInput,
  MatchBox,
  MatchCandidate,
  MatchCounterInput,
  MatchRequest,
  MatchRequestInput,
  MockCampaign,
  NewsletterFormValues,
  NewsletterSubscription,
  Notification,
  PublicEventFilters,
  RegisterInput,
  SiteAnalyticsSummary,
  TrackPageViewInput,
  TrackRegistrationClickInput,
  UserAccount,
  UserRole,
} from '../types';

type AuthTokenResponse = AuthSessionSnapshot & {
  token: string;
};

type PublicEventsResponse = {
  items: Event[];
  catalogs: EventCatalogs;
};

type APIErrorPayload = {
  error?: string;
};

export type EnrichedMatchRequest = MatchRequest & {
  sourceEvent: Event;
  targetEvent: Event;
  convertedEvent: Event | null;
  initiator: UserAccount;
  targetUser: UserAccount;
  awaitingUser: UserAccount | null;
};

const LEGACY_MOCK_STORAGE_KEY = 'frog-mock-state-v2';
const TOKEN_STORAGE_KEY = 'frog-auth-token-v1';

const resolveAPIBaseURL = () => {
  const envURL = process.env.REACT_APP_API_BASE_URL?.trim();
  if (envURL) {
    return envURL.replace(/\/$/, '');
  }

  if (typeof window !== 'undefined' && window.location.port === '3000') {
    return 'http://localhost:8080/api';
  }

  return '/api';
};

const api = axios.create({
  baseURL: resolveAPIBaseURL(),
  timeout: 30000,
});

const clearLegacyMockState = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(LEGACY_MOCK_STORAGE_KEY);
};

const readToken = () => {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(TOKEN_STORAGE_KEY) || '';
};

const persistToken = (token: string) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
};

const clearToken = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
};

const isAxiosError = (error: unknown): error is AxiosError<APIErrorPayload> =>
  axios.isAxiosError(error);

const isAuthError = (error: unknown) =>
  isAxiosError(error) &&
  (error.response?.status === 401 || error.response?.status === 403);

const normalizeError = (error: unknown) => {
  if (isAxiosError(error)) {
    const message = error.response?.data?.error?.trim();
    if (message) {
      return new Error(message);
    }

    if (error.code === 'ECONNABORTED') {
      return new Error('O backend demorou demais para responder.');
    }

    if (!error.response) {
      return new Error('Não foi possível conectar ao backend.');
    }
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error('Erro inesperado ao comunicar com o backend.');
};

const get = async <T>(url: string, params?: Record<string, string | undefined>) => {
  try {
    const response = await api.get<T>(url, { params });
    return response.data;
  } catch (error) {
    throw normalizeError(error);
  }
};

const post = async <T>(url: string, payload?: unknown) => {
  try {
    const response = await api.post<T>(url, payload);
    return response.data;
  } catch (error) {
    throw normalizeError(error);
  }
};

const put = async <T>(url: string, payload?: unknown) => {
  try {
    const response = await api.put<T>(url, payload);
    return response.data;
  } catch (error) {
    throw normalizeError(error);
  }
};

const patch = async <T>(url: string, payload?: unknown) => {
  try {
    const response = await api.patch<T>(url, payload);
    return response.data;
  } catch (error) {
    throw normalizeError(error);
  }
};

const postNoContent = async (url: string, payload?: unknown) => {
  try {
    await api.post(url, payload);
  } catch (error) {
    throw normalizeError(error);
  }
};

api.interceptors.request.use((config) => {
  const token = readToken();
  if (token) {
    const headers = AxiosHeaders.from(config.headers || {});
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }

  return config;
});

clearLegacyMockState();

const backendClient = {
  auth: {
    getSession: async (): Promise<AuthSessionSnapshot> => {
      if (!readToken()) {
        return { session: null, user: null };
      }

      try {
        const response = await api.get<AuthSessionSnapshot>('/auth/session');
        return response.data;
      } catch (error) {
        if (isAuthError(error)) {
          clearToken();
          return { session: null, user: null };
        }

        throw normalizeError(error);
      }
    },

    login: async (payload: LoginInput): Promise<AuthSessionSnapshot> => {
      try {
        const response = await api.post<AuthTokenResponse>('/auth/login', payload);
        persistToken(response.data.token);
        return {
          session: response.data.session,
          user: response.data.user,
        };
      } catch (error) {
        throw normalizeError(error);
      }
    },

    register: async (payload: RegisterInput): Promise<AuthSessionSnapshot> => {
      try {
        const response = await api.post<AuthTokenResponse>('/auth/register', payload);
        persistToken(response.data.token);
        return {
          session: response.data.session,
          user: response.data.user,
        };
      } catch (error) {
        throw normalizeError(error);
      }
    },

    logout: async (): Promise<void> => {
      try {
        await api.post('/auth/logout');
      } catch (error) {
        if (!isAuthError(error)) {
          throw normalizeError(error);
        }
      } finally {
        clearToken();
      }
    },

    listUsers: async (): Promise<UserAccount[]> => get<UserAccount[]>('/auth/users'),

    updateUserRole: async (userId: string, role: UserRole): Promise<UserAccount> =>
      patch<UserAccount>(`/auth/users/${userId}/role`, { role }),
  },

  events: {
    getCatalogs: async (): Promise<EventCatalogs> => get<EventCatalogs>('/events/catalogs'),

    listPublicEvents: async (
      filters: PublicEventFilters = {}
    ): Promise<PublicEventsResponse> =>
      get<PublicEventsResponse>('/events/public', {
        query: filters.query,
        institution: filters.institution,
        format: filters.format,
      }),

    listCreatorEvents: async (
      _userId: string,
      filters: CreatorEventFilters = {}
    ): Promise<Event[]> =>
      get<Event[]>('/events/creator', {
        query: filters.query,
        status: filters.status,
      }),

    listCurationEvents: async (): Promise<Event[]> => get<Event[]>('/events/curation'),

    getEventById: async (eventId: string): Promise<Event | null> => {
      try {
        return await get<Event>(`/events/${eventId}`);
      } catch (error) {
        if (error instanceof Error && error.message === 'Evento não encontrado.') {
          return null;
        }

        throw error;
      }
    },

    createEvent: async (_ownerId: string, payload: EventFormValues): Promise<Event> =>
      post<Event>('/events', payload),

    updateEvent: async (
      eventId: string,
      _actorId: string,
      payload: EventFormValues
    ): Promise<Event> => put<Event>(`/events/${eventId}`, payload),

    submitEvent: async (eventId: string, _actorId: string): Promise<Event> =>
      post<Event>(`/events/${eventId}/submit`),

    approveEvent: async (eventId: string, _adminId: string, message?: string): Promise<Event> =>
      post<Event>(`/events/${eventId}/approve`, message ? { message } : undefined),

    requestChanges: async (eventId: string, _adminId: string, message: string): Promise<Event> =>
      post<Event>(`/events/${eventId}/request-changes`, { message }),

    rejectEvent: async (eventId: string, _adminId: string, message: string): Promise<Event> =>
      post<Event>(`/events/${eventId}/reject`, { message }),

    cancelEvent: async (eventId: string, _actorId: string, message?: string): Promise<Event> =>
      post<Event>(`/events/${eventId}/cancel`, message ? { message } : undefined),
  },

  newsletter: {
    subscribe: async (payload: NewsletterFormValues): Promise<NewsletterSubscription> =>
      post<NewsletterSubscription>('/newsletter/subscribe', payload),

    unsubscribe: async (email: string): Promise<void> =>
      postNoContent('/newsletter/unsubscribe', { email }),

    updatePreferences: async (
      subscriptionId: string,
      payload: NewsletterFormValues
    ): Promise<NewsletterSubscription> =>
      put<NewsletterSubscription>(`/newsletter/${subscriptionId}/preferences`, payload),

    listSubscriptions: async (): Promise<NewsletterSubscription[]> =>
      get<NewsletterSubscription[]>('/newsletter/subscriptions'),

    listMockCampaigns: async (): Promise<MockCampaign[]> =>
      get<MockCampaign[]>('/newsletter/campaigns'),
  },

  matches: {
    searchMatchCandidates: async (filters: {
      query?: string;
      eventId?: string;
    }): Promise<MatchCandidate[]> =>
      get<MatchCandidate[]>('/matches/candidates', {
        query: filters.query,
        eventId: filters.eventId,
      }),

    listCreatorMatches: async (_userId: string, box: MatchBox): Promise<EnrichedMatchRequest[]> =>
      get<EnrichedMatchRequest[]>('/matches/creator', { box }),

    listAdminMatches: async (): Promise<EnrichedMatchRequest[]> =>
      get<EnrichedMatchRequest[]>('/matches/admin'),

    createMatchRequest: async (payload: MatchRequestInput): Promise<EnrichedMatchRequest> =>
      post<EnrichedMatchRequest>('/matches', payload),

    counterMatchRequest: async (
      matchId: string,
      _actorId: string,
      payload: MatchCounterInput
    ): Promise<EnrichedMatchRequest> =>
      post<EnrichedMatchRequest>(`/matches/${matchId}/counter`, payload),

    acceptMatchRequest: async (
      matchId: string,
      _actorId: string,
      note?: string
    ): Promise<EnrichedMatchRequest> =>
      post<EnrichedMatchRequest>(`/matches/${matchId}/accept`, note ? { note } : {}),

    declineMatchRequest: async (
      matchId: string,
      _actorId: string,
      note?: string
    ): Promise<EnrichedMatchRequest> =>
      post<EnrichedMatchRequest>(`/matches/${matchId}/decline`, note ? { note } : {}),
  },

  notifications: {
    listNotifications: async (_userId: string): Promise<Notification[]> =>
      get<Notification[]>('/notifications'),

    markNotificationRead: async (notificationId: string): Promise<void> => {
      await patch(`/notifications/${notificationId}/read`);
    },
  },

  analytics: {
    trackPageView: async (payload: TrackPageViewInput): Promise<void> => {
      await postNoContent('/analytics/pageviews', payload);
    },

    trackRegistrationClick: async (payload: TrackRegistrationClickInput): Promise<void> => {
      await postNoContent('/analytics/registration-clicks', payload);
    },

    getSiteAnalytics: async (days = 30): Promise<SiteAnalyticsSummary> =>
      get<SiteAnalyticsSummary>('/analytics/summary', { days: String(days) }),
  },
};

export const mockBackend = backendClient;
